import {
    collection,
    addDoc,
    query,
    where,
    orderBy,
    getDocs,
    serverTimestamp,
    limit
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { authService } from './authService';
import { adminService } from './adminService';

export interface Message {
    id?: string;
    senderId: string;
    senderName: string;
    recipientId: string | 'all' | 'admins'; // 'admins' for user->admin, 'all' for broadcast
    subject: string;
    content: string;
    createdAt: any;
    read: boolean;
    type: 'direct' | 'broadcast' | 'system';
}

export const messageService = {
    // For users to message admins
    async sendMessageToAdmins(subject: string, content: string) {
        const user = authService.getCurrentUser();
        if (!user) throw new Error('Must be logged in');

        try {
            await addDoc(collection(db, 'messages'), {
                senderId: user.id,
                senderName: user.name,
                recipientId: 'admins',
                subject,
                content,
                createdAt: serverTimestamp(),
                read: false,
                type: 'direct'
            });
        } catch (error) {
            console.error('Error sending message to admins', error);
            throw new Error('Failed to send message');
        }
    },

    // For admins to message a specific user
    async sendMessageToUser(userId: string, subject: string, content: string) {
        const admin = authService.getCurrentUser();
        if (!admin) throw new Error('Must be logged in as admin');

        try {
            await addDoc(collection(db, 'messages'), {
                senderId: admin.id,
                senderName: admin.name, // "Admin Team" or specific admin name
                recipientId: userId,
                subject,
                content,
                createdAt: serverTimestamp(),
                read: false,
                type: 'direct'
            });
            await adminService.logAction('SEND_MESSAGE', `Sent message to user ${userId}`, userId);
        } catch (error) {
            console.error('Error sending message to user', error);
            throw new Error('Failed to send message');
        }
    },

    // Broadcast to all users
    async broadcastMessage(subject: string, content: string) {
        const admin = authService.getCurrentUser();
        if (!admin) throw new Error('Must be logged in as admin');

        try {
            await addDoc(collection(db, 'messages'), {
                senderId: admin.id,
                senderName: 'Fibre Admin Team',
                recipientId: 'all',
                subject,
                content,
                createdAt: serverTimestamp(),
                read: false,
                type: 'broadcast'
            });
            await adminService.logAction('BROADCAST_MESSAGE', 'Sent broadcast message', 'all');
        } catch (error) {
            console.error('Error broadcasting message', error);
            throw new Error('Failed to broadcast');
        }
    },

    // Get messages for current user
    async getMyMessages() {
        const user = authService.getCurrentUser();
        if (!user) return [];

        try {
            // Get direct messages AND broadcasts
            const directQ = query(
                collection(db, 'messages'),
                where('recipientId', '==', user.id)
            );

            const sentQ = query(
                collection(db, 'messages'),
                where('senderId', '==', user.id)
            );

            const broadcastQ = query(
                collection(db, 'messages'),
                where('recipientId', '==', 'all'),
                limit(50)
            );

            const [directSnap, sentSnap, broadcastSnap] = await Promise.all([
                getDocs(directQ),
                getDocs(sentQ),
                getDocs(broadcastQ)
            ]);

            const messages = [
                ...directSnap.docs.map(d => ({ id: d.id, ...d.data() } as Message)),
                ...sentSnap.docs.map(d => ({ id: d.id, ...d.data() } as Message)),
                ...broadcastSnap.docs.map(d => ({ id: d.id, ...d.data() } as Message))
            ];

            // Remove duplicates (e.g. if I sent a message and then it shows up in recipient check, though unlikely here)
            const uniqueMessages = Array.from(new Map(messages.map(m => [m.id, m])).values());

            // Client-side sort: handle both server timestamps and numbers
            return uniqueMessages.sort((a, b) => {
                const timeA = a.createdAt?.toMillis?.() || a.createdAt || 0;
                const timeB = b.createdAt?.toMillis?.() || b.createdAt || 0;
                return timeB - timeA;
            });
        } catch (error) {
            console.error('Error fetching messages', error);
            return [];
        }
    },

    // Admin: Get all messages sent to admins
    async getAdminInbox() {
        try {
            const q = query(
                collection(db, 'messages'),
                where('recipientId', '==', 'admins')
            );
            const snap = await getDocs(q);
            const messages = snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Message));

            return messages.sort((a, b) => {
                const timeA = a.createdAt?.toMillis?.() || a.createdAt || 0;
                const timeB = b.createdAt?.toMillis?.() || b.createdAt || 0;
                return timeB - timeA;
            });
        } catch (error) {
            console.error('Error fetching admin inbox', error);
            return [];
        }
    }
};
